import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Button} from '@ui/buttons/button';
import {Link} from 'react-router';
import {apiClient} from '@common/http/query-client';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import type {BlockInstance} from '../appearance-data';
import {CountryFlag, displayCountryName} from '@common/ui/country/country-display';
import {EyebrowPill} from './eyebrow-pill';
import {Reveal} from '../motion';
import {accent} from './accents';
import {Section, SectionHeading} from './section-kit';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';

/** Base URL for the bundled /flags/{iso2}.svg assets. */
const flagBase = getBootstrapData().settings.base_url || '';

type P = {block: BlockInstance; props: Record<string, any>};
const arr = (v: any): any[] => (Array.isArray(v) ? v : []);
const colsClass = (c: string) =>
  c === '5' ? 'md:grid-cols-5' : c === '4' ? 'md:grid-cols-4' : c === '2' ? 'md:grid-cols-2' : 'md:grid-cols-3';

export function HowItWorksBlock({props}: P) {
  const steps = arr(props.steps);
  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[1100px]">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className="grid grid-cols-1 gap-24 md:grid-cols-3">
          {steps.map((s, i) => (
            <div key={i} className="relative rounded-2xl border border-divider bg-paper p-24 text-center">
              <div className="mx-auto mb-12 flex h-44 w-44 items-center justify-center rounded-full bg-primary/10 text-lg font-bold text-primary">{i + 1}</div>
              {s.icon ? <span className="material-icons mb-8 text-3xl text-primary">{s.icon}</span> : null}
              {s.title ? <h3 className="mb-6 font-semibold">{s.title}</h3> : null}
              {s.text ? <p className="text-sm text-muted">{s.text}</p> : null}
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

export function WhyUsBlock({props}: P) {
  const reasons = arr(props.reasons);
  return (
    <section className="px-24 py-56">
      <div className="mx-auto grid max-w-[1100px] items-center gap-32 md:grid-cols-2">
        <div>
          <EyebrowPill text={props.eyebrow} />
          {props.heading ? <h2 className="mb-12 text-2xl font-bold md:text-3xl">{props.heading}</h2> : null}
          {props.subheading ? <p className="mb-20 text-muted">{props.subheading}</p> : null}
          <div className="space-y-16">
            {reasons.map((r, i) => (
              <div key={i} className="flex gap-12">
                {r.icon ? <span className="material-icons text-primary">{r.icon}</span> : null}
                <div>
                  {r.title ? <h3 className="font-semibold">{r.title}</h3> : null}
                  {r.text ? <p className="text-sm text-muted">{r.text}</p> : null}
                </div>
              </div>
            ))}
          </div>
        </div>
        {props.image ? <img src={props.image} alt="" className="w-full rounded-2xl object-cover" /> : null}
      </div>
    </section>
  );
}

export function ServicesBlock({props}: P) {
  const items = arr(props.items);
  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[1100px]">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className={`grid grid-cols-1 gap-24 ${colsClass(props.columns)}`}>
          {items.map((s, i) => {
            const a = accent(i);
            const inner = (
              <div className="ap-lift ap-shine h-full rounded-2xl border border-divider bg-paper p-24 shadow-sm transition hover:border-primary hover:shadow-lg">
                {s.image ? (
                  <img src={s.image} alt="" className="mb-14 h-52 w-52 rounded-2xl object-contain" />
                ) : s.icon ? (
                  <span className="mb-14 flex h-52 w-52 items-center justify-center rounded-2xl"
                    style={{backgroundColor: a.bg, color: a.fg}}>
                    <span className="material-icons text-3xl">{s.icon}</span>
                  </span>
                ) : null}
                {s.title ? <h3 className="mb-8 font-semibold">{s.title}</h3> : null}
                {s.text ? <p className="text-sm text-muted">{s.text}</p> : null}
              </div>
            );
            return (
              <Reveal key={i} index={i}>
                {s.link ? <Link to={s.link}>{inner}</Link> : <div>{inner}</div>}
              </Reveal>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

export function BeforeAfterBlock({props}: P) {
  const [pos, setPos] = useState(50);
  if (!props.before_image || !props.after_image) return null;
  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[820px]">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className="relative select-none overflow-hidden rounded-2xl">
          <img src={props.after_image} alt={props.after_label} className="block w-full" />
          <div className="absolute inset-0 overflow-hidden" style={{width: `${pos}%`}}>
            <img src={props.before_image} alt={props.before_label} className="h-full w-full object-cover" style={{maxWidth: 'none', width: '100vw'}} />
            <span className="absolute bottom-8 start-8 rounded bg-black/60 px-6 py-2 text-2xs text-white">{props.before_label}</span>
          </div>
          <span className="absolute bottom-8 end-8 rounded bg-black/60 px-6 py-2 text-2xs text-white">{props.after_label}</span>
          <input type="range" min={0} max={100} value={pos} onChange={e => setPos(+e.target.value)}
            className="absolute inset-x-0 bottom-1/2 w-full cursor-ew-resize appearance-none bg-transparent" />
        </div>
      </div>
    </Section>
  );
}

export function VideoBlock({props}: P) {
  const url: string = props.url || '';
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  const embed = yt ? `https://www.youtube.com/embed/${yt[1]}` : vimeo ? `https://player.vimeo.com/video/${vimeo[1]}` : '';
  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[960px]">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className="aspect-video overflow-hidden rounded-2xl bg-black">
          {embed ? (
            <iframe src={embed} className="h-full w-full" allowFullScreen title={props.heading || 'video'} />
          ) : url ? (
            <video src={url} poster={props.poster || undefined} controls className="h-full w-full" />
          ) : null}
        </div>
      </div>
    </Section>
  );
}

export function PricingBlock({props}: P) {
  const plans = arr(props.plans);
  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[1100px]">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className="grid grid-cols-1 gap-24 md:grid-cols-3">
          {plans.map((p, i) => {
            const featured = String(p.featured).toLowerCase() === 'yes';
            const features = String(p.features || '').split('\n').map(s => s.trim()).filter(Boolean);
            return (
              <div key={i} className={`rounded-2xl border p-24 ${featured ? 'border-primary shadow-lg ring-1 ring-primary' : 'border-divider'}`}>
                {p.name ? <h3 className="text-lg font-bold">{p.name}</h3> : null}
                <p className="my-12"><span className="text-3xl font-bold text-primary">{p.price}</span>{p.period ? <span className="text-sm text-muted"> /{p.period}</span> : null}</p>
                <ul className="mb-16 space-y-6 text-sm text-muted">
                  {features.map((f, j) => <li key={j}>✓ {f}</li>)}
                </ul>
                {p.button_label ? (
                  <Button variant={featured ? 'flat' : 'outline'} color="primary" className="w-full rounded-full"
                    elementType={Link} to={p.button_url || '/'}>{p.button_label}</Button>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

export function PartnersBlock({props}: P) {
  const logos = arr(props.logos).filter(l => l.image);
  if (!logos.length) return null;
  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[1100px] text-center">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className="flex flex-wrap items-center justify-center gap-32 opacity-80">
          {logos.map((l, i) => {
            const img = <img src={l.image} alt={l.alt || ''} className="h-40 w-auto object-contain grayscale transition hover:grayscale-0" />;
            return l.link ? <a key={i} href={l.link} target="_blank" rel="noopener noreferrer">{img}</a> : <span key={i}>{img}</span>;
          })}
        </div>
      </div>
    </Section>
  );
}

export function ContactCtaBlock({props}: P) {
  return (
    <section className="px-24 py-56" style={{backgroundColor: props.bg_color || '#0b3d2e', color: props.text_color || '#fff'}}>
      <div className="mx-auto flex max-w-[960px] flex-col items-center gap-12 text-center">
        <EyebrowPill text={props.eyebrow} />
        {props.heading ? <h2 className="text-2xl font-bold md:text-3xl">{props.heading}</h2> : null}
        {props.text ? <p className="max-w-720 opacity-90">{props.text}</p> : null}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-16 text-sm">
          {props.phone ? <a href={`tel:${props.phone}`} className="flex items-center gap-6"><span className="material-icons text-base">call</span>{props.phone}</a> : null}
          {props.email ? <a href={`mailto:${props.email}`} className="flex items-center gap-6"><span className="material-icons text-base">mail</span>{props.email}</a> : null}
        </div>
        {props.button_label ? (
          <Button variant="flat" color="primary" size="lg" className="mt-8 rounded-full"
            elementType={Link} to={props.button_url || '/contact'}>{props.button_label}</Button>
        ) : null}
      </div>
    </section>
  );
}

export function TimelineBlock({props}: P) {
  const items = arr(props.items);
  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color}>
      <div className="mx-auto max-w-720">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <ol className="relative border-s-2 border-divider ps-24">
          {items.map((it, i) => (
            <li key={i} className="mb-24">
              <span className="absolute -start-[9px] mt-1 h-4 w-4 rounded-full bg-primary" />
              {it.date ? <p className="text-2xs font-semibold uppercase text-primary">{it.date}</p> : null}
              {it.title ? <h3 className="font-semibold">{it.title}</h3> : null}
              {it.text ? <p className="text-sm text-muted">{it.text}</p> : null}
            </li>
          ))}
        </ol>
      </div>
    </Section>
  );
}

interface CountryCard {
  flag: string;
  name: string;
  meta: string;
  link: string;
}

export function CountryCardsBlock({props}: P) {
  // Manual mode now also resolves flags/names/counts from live data (hand-picked
  // codes), so we need the country list in both modes. Legacy manual blocks that
  // still carry a free-text `items` array fall back to it.
  const manual = props.source === 'manual';
  const codes: string[] = Array.isArray(props.codes) ? props.codes : [];
  const legacyItems = arr(props.items) as CountryCard[];
  const {localeCode} = useSelectedLocale();

  const {data: countryData} = useQuery({
    queryKey: ['country-cards', manual ? 'all' : props.limit || 6],
    queryFn: () =>
      apiClient
        .get('appearance/top-countries', {params: manual ? {all: 1} : {limit: props.limit || 6}})
        .then(r => (r.data.items ?? r.data.data?.items ?? []) as {code: string; count: number}[]),
    staleTime: 5 * 60 * 1000,
  });

  // Localized country name from the ISO code (also re-localizes on lang switch).
  // Goes through `displayCountryName` so the NAME_OVERRIDES table applies —
  // calling Intl.DisplayNames directly yields "China mainland" for CN on newer
  // ICU builds (Apple/Safari), i.e. a different name per device.
  const loc = String(localeCode || 'en').toLowerCase().split('-')[0];
  const regionNames = {of: (c: string) => displayCountryName(c, c, loc)};

  const toCard = (code: string, count: number): CountryCard => ({
    flag: code,
    name: (regionNames?.of(code) || code) as string,
    meta: `${count.toLocaleString()} ${localeCode === 'ar' ? 'فرصة' : 'Opportunities'}`,
    link: `/opportunities?country_code=${code}`,
  });

  let items: CountryCard[];
  if (manual) {
    if (codes.length) {
      const counts = new Map((countryData ?? []).map(c => [c.code, c.count]));
      items = codes.map(code => toCard(code, counts.get(code) ?? 0));
    } else {
      items = legacyItems; // backward-compat with old free-text manual lists
    }
  } else {
    items = (countryData ?? []).map(c => toCard(c.code, c.count));
  }

  if (!items.length) return null;

  // One row on desktop: column count follows the number of countries (capped).
  const oneRow = props.layout !== 'grid';
  const n = Math.min(items.length, 6);
  const colsMd = oneRow
    ? (['', '', 'md:grid-cols-2', 'md:grid-cols-3', 'md:grid-cols-4', 'md:grid-cols-5', 'md:grid-cols-6'][n] || 'md:grid-cols-6')
    : colsClass(props.columns);

  return (
    // Education-themed animated ground: drifting book / cap / globe glyphs over
    // a faint dot grid. `bg_style` from the block settings can override it.
    <Section index={props.__index} bgStyle={props.bg_style || 'edu'} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[1100px]">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className={`grid grid-cols-2 gap-16 ${colsMd}`}>
          {items.map((c, i) => {
            const raw = String(c.flag || '').trim();
            const isUrl = raw.startsWith('http');
            const isCode = /^[a-z]{2}$/i.test(raw); // ISO-2 like "tr", "de"
            // Photo-forward card: the flag fills the tile as artwork behind a
            // dark scrim, with the name/count in white on top — far more
            // eye-catching than a small flag chip on a white card.
            // Flags are landscape (mostly 3:2) but the card is portrait, so
            // object-cover chopped the left/right off every flag. Instead:
            // a blurred, scaled copy fills the tile as a colour ground, and the
            // real flag sits on top with object-contain — always whole.
            const flagSrc = isCode
              ? `${flagBase}/flags/${raw.toLowerCase()}.svg`
              : isUrl
                ? raw
                : null;

            // Clean neutral card: a soft grey stage holds the whole flag, with
            // the label on white below. (An earlier version used a blurred copy
            // of the flag as the background — it turned every card into a
            // saturated red/orange blob.)
            const inner = (
              <div className="ap-lift group flex h-full flex-col overflow-hidden rounded-2xl border border-divider bg-paper shadow-sm transition hover:border-primary hover:shadow-lg">
                <div className="relative flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 p-20 dark:from-white/5 dark:to-white/10">
                  {flagSrc ? (
                    <img src={flagSrc} alt={c.name || ''} loading="lazy"
                      className="h-auto w-full max-w-[110px] rounded-lg object-contain shadow-md ring-1 ring-black/10 transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <span className="text-5xl">{raw || '🌍'}</span>
                  )}
                </div>
                <div className="flex flex-1 flex-col justify-center px-14 py-12 text-start">
                  {c.name ? (
                    <p className="text-sm font-bold leading-tight group-hover:text-primary">{c.name}</p>
                  ) : null}
                  {c.meta ? (
                    <p className="mt-2 text-xs font-semibold text-primary">{c.meta}</p>
                  ) : null}
                </div>
              </div>
            );
            return (
              <Reveal key={i} index={i}>
                {c.link ? <Link to={c.link}>{inner}</Link> : <div>{inner}</div>}
              </Reveal>
            );
          })}
        </div>
      </div>
    </Section>
  );
}
